/**
 * Budget start (TMMAPP-0027 / TMMAPI-0058; TMMAPP-0130 item 10). The account page shows one quiet
 * line under the subtitle. CHANGE opens this dialog. Wire: `budgetStartOn` / `historicalLineCount`
 * on account reads; PATCH `{ budgetStartOn }` (null clears).
 */
import { useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { InfoIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { resolveTokens } from '@/theme/theme';

export interface BudgetStartLineProps {
  budgetStartOn: string | null;
  historicalLineCount: number;
  canChange: boolean;
  /** Civil date the books last matched the bank. Nothing is said while null. */
  balancedThroughOn: string | null;
  onSave: (next: string | null) => Promise<void>;
}

/**
 * The 12px line under the account subtitle: "Budgeting from {date} CHANGE · {n} history lines ·
 * Balanced through {date} ⓘ" (TMMAPP-0134 item 8: CHANGE sits beside the date it changes). No
 * start date: the import sentence, and no CHANGE.
 */
export function BudgetStartLine({
  budgetStartOn,
  historicalLineCount,
  canChange,
  balancedThroughOn,
  onSave,
}: BudgetStartLineProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const [open, setOpen] = useState(false);
  const [about, setAbout] = useState<HTMLElement | null>(null);
  const count = Math.max(0, historicalLineCount);
  const ink = { m: 0, fontSize: 12, lineHeight: '16px', color: tokens.textLabel('bg') };
  const balanced =
    balancedThroughOn === null
      ? null
      : t('budget.balancedThrough', { date: formatCivilDate(balancedThroughOn, locale) });

  if (budgetStartOn === null && balanced === null) {
    return (
      <Typography component="p" sx={ink}>
        {t('budget.startsWithImport')}
      </Typography>
    );
  }

  const rest = [
    ...(budgetStartOn !== null && count > 0 ? [t('budget.historyCount', { count })] : []),
    ...(balanced !== null ? [balanced] : []),
  ];

  return (
    <>
      <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography component="p" sx={ink}>
          {budgetStartOn === null
            ? t('budget.startsWithImport')
            : t('budget.from', { date: formatCivilDate(budgetStartOn, locale) })}
        </Typography>
        {canChange && budgetStartOn !== null && (
          <Button
            size="small"
            variant="text"
            aria-label={t('budget.changeLabel')}
            onClick={() => setOpen(true)}
            sx={{ fontSize: 12, minWidth: 0, p: 0.5 }}
          >
            {t('budget.change')}
          </Button>
        )}
        {rest.length > 0 && (
          <Typography component="p" sx={ink}>
            {rest.map((part) => ` · ${part}`).join('')}
          </Typography>
        )}
        <IconButton
          size="small"
          aria-label={t('budget.about')}
          onClick={(event) => setAbout(event.currentTarget)}
        >
          <InfoIcon />
        </IconButton>
      </Stack>
      <Popover
        open={about !== null}
        anchorEl={about}
        onClose={() => setAbout(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Typography sx={{ p: 2, maxWidth: 360, fontSize: 14 }}>
          {budgetStartOn === null
            ? null
            : count > 0
              ? t('budget.aboutHistory', { count })
              : t('budget.aboutNone')}
          {balancedThroughOn !== null && (
            <>
              {budgetStartOn !== null ? ' ' : null}
              {t('budget.aboutBalanced', {
                date: formatCivilDate(balancedThroughOn, locale),
              })}
            </>
          )}
        </Typography>
      </Popover>
      {open && (
        <BudgetStartDialog
          initial={budgetStartOn}
          onClose={() => setOpen(false)}
          onSave={async (next) => {
            await onSave(next);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

interface BudgetStartDialogProps {
  initial: string | null;
  onClose: () => void;
  onSave: (next: string | null) => Promise<void>;
}

export function BudgetStartDialog({ initial, onClose, onSave }: BudgetStartDialogProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial ?? '');
  // Which write is in flight: the Save button shows the loading state for a save only.
  const [pending, setPending] = useState<'save' | 'clear' | null>(null);
  const busy = pending !== null;
  const { dirty } = useDirty(value);
  const guard = useDialogDirtyGuard(dirty, onClose, busy);

  async function handleSave() {
    if (busy) return;
    setPending('save');
    try {
      await onSave(value.length > 0 ? value : null);
    } finally {
      setPending(null);
    }
  }

  async function handleClear() {
    if (busy) return;
    setPending('clear');
    try {
      await onSave(null);
    } finally {
      setPending(null);
    }
  }

  return (
    <GuardedDialog guard={guard} fullWidth maxWidth="xs">
      <DialogTitle>{t('budget.starts')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            type="date"
            label={t('budget.starts')}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
          <Typography variant="body2" color="text.secondary">
            {t('budget.helpBack')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('budget.helpForward')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('budget.clearHelp')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={guard.requestClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button onClick={() => void handleClear()} disabled={busy}>
          {t('budget.clear')}
        </Button>
        <SubmitButton
          loading={pending === 'save'}
          onClick={() => void handleSave()}
          disabled={busy}
        >
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </GuardedDialog>
  );
}
